import { useEffect, useState, useCallback, useRef } from "react";
import { ColumnActionLogWithUserSchema } from "../models/audit/columnActionLog";
import { fetchFastApi } from "../api/fastapi";
import { toast } from "sonner";
import { z } from "zod";

// Returned for a path that has nothing loaded, so every such render hands back
// the same array rather than a fresh empty one.
const NO_LOGS: z.infer<typeof ColumnActionLogWithUserSchema>[] = [];

interface UseColumnActionLogsProps {
  checkResultId?: string;
  attributeTestResultId?: string;
  analyticsResultId?: string;
  fetchOnLoad?: boolean;
}

/**
 * The route for whichever result kind was named, or `null` when none was.
 *
 * Each of the three is scoped by the verdict's column *definition* on the
 * backend, so the panel shows the column's action history rather than one row's —
 * any verdict on a cell, live or superseded, resolves to the same log set.
 */
function columnActionLogPath({
  checkResultId,
  attributeTestResultId,
  analyticsResultId,
}: UseColumnActionLogsProps): string | null {
  if (checkResultId) {
    return `/matching/column-action-logs/check-result/${checkResultId}`;
  }
  if (attributeTestResultId) {
    return `/matching/column-action-logs/attribute-test-result/${attributeTestResultId}`;
  }
  if (analyticsResultId) {
    return `/matching/column-action-logs/analytics-result/${analyticsResultId}`;
  }
  return null;
}

export const useColumnActionLogs = ({
  checkResultId,
  attributeTestResultId,
  analyticsResultId,
  fetchOnLoad = true,
}: UseColumnActionLogsProps) => {
  const [loadedLogs, setLoadedLogs] = useState<
    z.infer<typeof ColumnActionLogWithUserSchema>[]
  >([]);
  const [loading, setLoading] = useState(false);
  // The path whose logs are held in `loadedLogs`. The auto-fetch effect compares
  // it against the current path, so switching to another result re-arms the
  // fetch rather than staying latched after the first load, and the returned
  // `logs` are derived from the same comparison so the two cannot disagree.
  const [loadedPath, setLoadedPath] = useState<string | null>(null);

  // The path collapses the three id kinds into one identity token: exactly one
  // id is ever set, and the response belongs to the path it was requested for.
  const path = columnActionLogPath({
    checkResultId,
    attributeTestResultId,
    analyticsResultId,
  });

  // The path the hook is currently rendering for.
  const currentPathRef = useRef(path);
  useEffect(() => {
    currentPathRef.current = path;
  }, [path]);

  // Requests are numbered in issue order: the highest number issued owns the
  // `loading` flag, and only that request may write logs.
  const requestIdRef = useRef(0);

  const fetchLogs = useCallback(async () => {
    if (!path) {
      return;
    }

    const requestId = ++requestIdRef.current;
    setLoading(true);
    try {
      const response = await fetchFastApi({
        method: "GET",
        path,
        query: {
          limit: "100",
          offset: "0",
        },
        throwOnErrorEnvelope: true,
      });
      // A newer request has been issued, or the answer is for a result no
      // longer on screen; either way it must not reach state.
      if (requestId !== requestIdRef.current) return;
      if (currentPathRef.current !== path) return;

      const data = ColumnActionLogWithUserSchema.array().parse(response.logs);
      setLoadedLogs(data);
      setLoadedPath(path);
    } catch (error) {
      if (requestId !== requestIdRef.current) return;
      if (currentPathRef.current !== path) return;
      console.error("Error fetching column action logs:", error);
      toast.error("Failed to fetch activity logs");
    } finally {
      // Reached even when the checks above return, so the newest request always
      // releases the spinner, including when its answer is dropped.
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, [path]);

  useEffect(() => {
    if (fetchOnLoad && path && loadedPath !== path) {
      void fetchLogs();
    }
  }, [fetchOnLoad, path, loadedPath, fetchLogs]);

  // A result shows its own history or nothing: logs belonging to a different
  // path are held but not displayed, and become visible again without a refetch
  // if that path is selected again.
  const logs = loadedPath === path ? loadedLogs : NO_LOGS;

  return {
    logs,
    loading,
    fetchLogs,
  };
};
